import { useState, useCallback, useRef, useEffect } from 'react';

interface CopyOptions {
  /**
   * Rich-text flavor written alongside the plain text. Apps that accept
   * pasted HTML (Slack, Google Docs, Gmail, Notion) render this instead of
   * the raw source, so formatted chat output pastes as formatted text.
   */
  html?: string;
}

/**
 * Writes `text/plain` and, when supplied, `text/html` in a single clipboard
 * item. Falls back to a plain-text write whenever the richer API is missing
 * (insecure context, older browser, jsdom) or rejects, so the copy still
 * lands — losing formatting is better than losing the copy.
 */
async function writeClipboard(text: string, html?: string): Promise<void> {
  const clipboard = navigator.clipboard;
  if (!clipboard) return;

  if (html && typeof clipboard.write === 'function' && typeof ClipboardItem !== 'undefined') {
    try {
      await clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return;
    } catch {
      // Fall through to the plain-text path below.
    }
  }

  await clipboard.writeText?.(text);
}

/**
 * Shared copy-to-clipboard state hook.
 * Returns { copied, copy } — call copy(text) and `copied` goes true for `timeout` ms.
 * Pass `{ html }` to also offer a rich-text flavor to the paste target.
 */
export function useCopyToClipboard(timeout = 1500) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const copy = useCallback(
    (text: string, options?: CopyOptions) => {
      // Swallow rejections (e.g. clipboard permission denied in headless
      // test environments or insecure contexts) so they don't surface as
      // unhandled promise rejections; the copied affordance still toggles.
      // `Promise.resolve` shields against stubbed clipboards (story/test
      // mocks) whose writeText doesn't return a promise — calling `.catch`
      // directly on such a return value throws inside the click handler.
      void Promise.resolve(writeClipboard(text, options?.html)).catch(() => {});
      if (timerRef.current) clearTimeout(timerRef.current);
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), timeout);
    },
    [timeout],
  );

  return { copied, copy };
}
